import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

const CACHE_TTL_MS = 60 * 1000; // 60s — matches plan-doc

export const fetchAttribution = createAsyncThunk(
  "attribution/fetch",
  async ({ period = "30d", orgId, jobIds } = {}, { getState, rejectWithValue }) => {
    const state = getState().attributionSliceReducer;
    // Skip refetch if we have fresh data for the same query
    const cacheKey = `${period}|${orgId || ""}|${(jobIds || []).join(",")}`;
    if (
      state.lastCacheKey === cacheKey &&
      state.lastFetchedAt &&
      Date.now() - state.lastFetchedAt < CACHE_TTL_MS
    ) {
      return { fromCache: true, data: state.data, cacheKey };
    }

    try {
      const params = new URLSearchParams();
      params.set("period", period);
      if (orgId != null) params.set("orgId", String(orgId));
      if (Array.isArray(jobIds) && jobIds.length > 0) {
        jobIds.forEach((j) => params.append("jobIds", String(j)));
      }
      const r = await axiosInstance.get(
        `/common/analytics/source-attribution?${params.toString()}`,
      );
      return { fromCache: false, data: r.data || {}, cacheKey };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load attribution");
    }
  },
);

const attributionSlice = createSlice({
  name: "attribution",
  initialState: {
    data: { channels: [], totalCandidates: 0, topSources: [], period: "30d" },
    status: "idle",
    error: null,
    lastFetchedAt: 0,
    lastCacheKey: "",
  },
  reducers: {
    invalidateAttribution(state) {
      state.lastFetchedAt = 0;
      state.lastCacheKey = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttribution.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAttribution.fulfilled, (state, { payload }) => {
        state.status = "succeeded";
        if (!payload.fromCache) {
          state.data = payload.data;
          state.lastFetchedAt = Date.now();
          state.lastCacheKey = payload.cacheKey;
        }
      })
      .addCase(fetchAttribution.rejected, (state, { payload }) => {
        state.status = "failed";
        state.error = payload || "Failed to load attribution";
      });
  },
});

export const { invalidateAttribution } = attributionSlice.actions;
export const selectAttribution = (s) => s.attributionSliceReducer.data;
export const selectAttributionStatus = (s) => s.attributionSliceReducer.status;
export const selectAttributionError = (s) => s.attributionSliceReducer.error;

export default attributionSlice.reducer;
